// frontend/src/manager_notifications/notificationUtils.js

import { 
  NOTIFICATION_TYPES, 
  PRIORITY_LEVELS,
  STATUS_CONFIG 
} from './notificationTypes';

/**
 * Formate une date en temps relatif (ex: "il y a 2 heures")
 */
export const formatRelativeTime = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'À l\'instant';
  if (diffMins < 60) return `il y a ${diffMins} min`;
  if (diffHours < 24) return `il y a ${diffHours} h`;
  if (diffDays === 1) return 'hier';
  if (diffDays < 7) return `il y a ${diffDays} jours`;
  
  return date.toLocaleDateString();
};

/**
 * Récupère la priorité d'un événement en fonction de ses propriétés
 */
export const getPriorityFromEvent = (event) => {
  if (event.priority === 'urgent') return 'critical';
  if (event.priority === 'high') return 'high';
  if (event.event_type === 'emergency') return 'critical';
  if (event.event_type === 'maintenance') return 'high';
  return 'normal';
};

/**
 * Retourne la classe CSS pour la couleur de priorité
 */
export const getPriorityColorClass = (priority) => {
  const classes = {
    critical: 'border-red-500 bg-red-500/10 text-red-400',
    high: 'border-orange-500 bg-orange-500/10 text-orange-400',
    normal: 'border-blue-500 bg-blue-500/10 text-blue-400',
    low: 'border-gray-500 bg-gray-500/10 text-gray-400'
  };
  return classes[priority] || classes.normal;
};

/**
 * Retourne la classe CSS pour la couleur de catégorie
 */
export const getCategoryColorClass = (category) => {
  const classes = {
    schedule: 'bg-blue-500/20 border-blue-500',
    alert: 'bg-red-500/20 border-red-500',
    chat: 'bg-green-500/20 border-green-500',
    system: 'bg-gray-500/20 border-gray-500'
  };
  return classes[category] || classes.system;
};

/**
 * Récupère l'icône pour un type de notification
 */
export const getNotificationIcon = (type) => {
  const typeConfig = NOTIFICATION_TYPES[type?.toUpperCase()];
  return typeConfig?.icon || 'Bell';
};

/**
 * Calcule le pourcentage de progression pour un événement
 */
export const getEventProgress = (event) => {
  let completed = 0;
  let total = 0;
  
  if (event.check_in !== undefined) {
    total++;
    if (event.check_in) completed++;
  }
  if (event.check_out !== undefined) {
    total++;
    if (event.check_out) completed++;
  }
  if (event.billing_status !== undefined) {
    total++;
    if (event.billing_status === 'paid') completed++;
  }
  if (event.grading) {
    total++;
    if (event.grading) completed++;
  }
  if (event.squawk) {
    total++;
    if (event.squawk) completed++;
  }
  
  return total > 0 ? (completed / total) * 100 : 0;
};

/**
 * Retourne le statut textuel d'un événement
 */
export const getEventStatusLabel = (event) => {
  const progress = getEventProgress(event);
  
  if (progress === 100) return 'Terminé';
  if (progress >= 66) return 'Presque terminé';
  if (progress >= 33) return 'En cours';
  if (progress > 0) return 'Commencé';
  return 'En attente';
};

/**
 * Regroupe les notifications par catégorie
 */
export const groupNotificationsByCategory = (notifications) => {
  const grouped = {};
  
  notifications.forEach(notification => {
    const category = notification.typeConfig.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(notification);
  });
  
  return grouped;
};

/**
 * Filtre les notifications par période
 */
export const filterByDateRange = (notifications, startDate, endDate) => {
  return notifications.filter(n => {
    const date = new Date(n.timestamp);
    return (!startDate || date >= startDate) && (!endDate || date <= endDate);
  });
};

/**
 * Recherche dans les notifications
 */
export const searchNotifications = (notifications, query) => {
  if (!query) return notifications;
  
  const lowerQuery = query.toLowerCase();
  return notifications.filter(n => 
    n.title?.toLowerCase().includes(lowerQuery) ||
    n.message?.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Génère un résumé textuel pour une notification
 */
export const getNotificationSummary = (notification) => {
  switch (notification.source) {
    case 'schedule':
      if (notification.isToday) {
        return `Aujourd'hui à ${new Date(notification.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      }
      return `Prévu le ${new Date(notification.timestamp).toLocaleDateString()}`;
    
    case 'alert':
      return `Envoyé ${formatRelativeTime(notification.timestamp)}`;
    
    case 'chat':
      if (notification.type === 'appointment_request') {
        return `Request for ${notification.metadata?.senderName || 'quelqu\'un'}`;
      }
      return `Message de ${notification.metadata?.senderName || 'inconnu'}`;
    
    default:
      return notification.timestampRelative;
  }
};

/**
 * Vérifie si une notification est urgente (nécessite une action immédiate)
 */
export const isUrgent = (notification) => {
  if (notification.priority === 'critical') return true;
  if (notification.type === 'emergency') return true;
  if (notification.type === 'appointment_request' && 
      notification.metadata?.appointmentData?.urgency === 'immediate') return true;
  if (notification.type === 'maintenance' && notification.priority === 'high') return true;
  return false;
};